import "./ManagePaymentsPopup.css";
import { translateReservationStatus } from "./reservationI18n";
import { formatReservationPaymentNotice, useReservationModalTranslation } from "./reservationModalI18n";
// ManagePaymentsPopup (Pilcore)
// Notes:
// - UI strings follow the PMS language preference
// - Stripe endpoints are the source of truth for card operations
// Logs: [PAYMENTS][POPUP]

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "../api/axiosInstance";
import { FiCheckCircle, FiCreditCard, FiX } from "react-icons/fi";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import { reservationBaseCentsToCurrencyCents } from "../currency/reservationCurrency";
import {
  currencyAmountStep,
  formatCurrencyAmount,
  getCurrencyMinorUnits,
  hasValidCurrencyPrecision,
} from "../currency/currencyPrecision";
import {
  buildStripeOperationSignature,
  completeStripeOperationIdempotencyKey,
  createResilientStripeOperationStorage,
  getOrCreateStripeOperationIdempotencyKey,
  stripeIdempotencyRequestConfig,
  stripeOperationKeyShouldRotate,
  stripeOperationSuccessNotice,
} from "./paymentOperationIdempotency";
import {
  refundStatusLabel,
  summarizeRefundEvents,
} from "./paymentStatusPresentation";

function centsToCurrencyInput(cents, currencyCode, catalog = []) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return "0";
  return (n / 100).toFixed(getCurrencyMinorUnits(currencyCode, catalog));
}

function eurToCents(value) {
  const n = Number(String(value ?? "").replace(",", "."));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

function moneyShapeToCents(value) {
  if (value && typeof value === "object" && Number.isFinite(Number(value.cents))) {
    return Number(value.cents);
  }

  if (value && typeof value === "object" && value.amount != null) {
    return eurToCents(value.amount) || 0;
  }

  return eurToCents(value) || 0;
}

function buildSuggestedAllocations(balance, paymentCents) {
  const reservations = Array.isArray(balance?.reservations) ? balance.reservations : [];
  const eligible = reservations
    .map((reservation) => {
      const reservationId = Number(reservation?.reservation_id);
      const dueCents = Math.max(0, moneyShapeToCents(reservation?.balance_due));
      const totalCents = Math.max(0, moneyShapeToCents(reservation?.total));
      const allocatedCents = moneyShapeToCents(reservation?.allocated_paid);

      return {
        reservation_id: reservationId,
        label: reservation?.locator || "",
        group_index: reservation?.group_index ?? null,
        total_cents: totalCents,
        allocated_cents: allocatedCents,
        due_cents: dueCents,
        suggested_cents: 0,
      };
    })
    .filter((row) => row.reservation_id && row.due_cents > 0);

  const targetCents = Math.max(0, Math.min(Number(paymentCents || 0), eligible.reduce(
    (sum, row) => sum + row.due_cents,
    0
  )));

  if (!eligible.length || !targetCents) return eligible;

  const dueTotal = eligible.reduce((sum, row) => sum + row.due_cents, 0);
  let assigned = 0;

  const withRemainders = eligible.map((row) => {
    const raw = (targetCents * row.due_cents) / dueTotal;
    const base = Math.min(row.due_cents, Math.floor(raw));
    assigned += base;
    return {
      ...row,
      suggested_cents: base,
      remainder: raw - base,
    };
  });

  let remainderCents = targetCents - assigned;
  withRemainders
    .sort((a, b) => b.remainder - a.remainder)
    .forEach((row) => {
      if (remainderCents <= 0) return;
      if (row.suggested_cents >= row.due_cents) return;
      row.suggested_cents += 1;
      remainderCents -= 1;
    });

  return withRemainders
    .sort((a, b) => {
      const ai = Number.isFinite(Number(a.group_index)) ? Number(a.group_index) : Number.MAX_SAFE_INTEGER;
      const bi = Number.isFinite(Number(b.group_index)) ? Number(b.group_index) : Number.MAX_SAFE_INTEGER;
      if (ai !== bi) return ai - bi;
      return Number(a.reservation_id) - Number(b.reservation_id);
    })
    .map(({ remainder, ...row }) => row);
}

function buildSuggestedRefundAllocations(balance, refundCents) {
  const reservations = Array.isArray(balance?.reservations) ? balance.reservations : [];
  const eligible = reservations
    .map((reservation) => {
      const reservationId = Number(reservation?.reservation_id);
      const allocatedCents = Math.max(0, moneyShapeToCents(reservation?.allocated_paid));
      const totalCents = Math.max(0, moneyShapeToCents(reservation?.total));
      const dueCents = Math.max(0, moneyShapeToCents(reservation?.balance_due));

      return {
        reservation_id: reservationId,
        label: reservation?.locator || "",
        group_index: reservation?.group_index ?? null,
        total_cents: totalCents,
        allocated_cents: allocatedCents,
        due_cents: dueCents,
        suggested_cents: 0,
      };
    })
    .filter((row) => row.reservation_id && row.allocated_cents > 0);

  const targetCents = Math.max(0, Math.min(Number(refundCents || 0), eligible.reduce(
    (sum, row) => sum + row.allocated_cents,
    0
  )));

  if (!eligible.length || !targetCents) return eligible;

  const allocatedTotal = eligible.reduce((sum, row) => sum + row.allocated_cents, 0);
  let assigned = 0;

  const withRemainders = eligible.map((row) => {
    const raw = (targetCents * row.allocated_cents) / allocatedTotal;
    const base = Math.min(row.allocated_cents, Math.floor(raw));
    assigned += base;
    return {
      ...row,
      suggested_cents: base,
      remainder: raw - base,
    };
  });

  let remainderCents = targetCents - assigned;
  withRemainders
    .sort((a, b) => b.remainder - a.remainder)
    .forEach((row) => {
      if (remainderCents <= 0) return;
      if (row.suggested_cents >= row.allocated_cents) return;
      row.suggested_cents += 1;
      remainderCents -= 1;
    });

  return withRemainders
    .sort((a, b) => {
      const ai = Number.isFinite(Number(a.group_index)) ? Number(a.group_index) : Number.MAX_SAFE_INTEGER;
      const bi = Number.isFinite(Number(b.group_index)) ? Number(b.group_index) : Number.MAX_SAFE_INTEGER;
      if (ai !== bi) return ai - bi;
      return Number(a.reservation_id) - Number(b.reservation_id);
    })
    .map(({ remainder, ...row }) => row);
}

function fmtDT(v, locale) {
  if (!v) return "-";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString(locale);
  } catch {
    return "-";
  }
}

function normalizeAxiosError(err) {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const msg =
    data?.error ||
    data?.message ||
    err?.message ||
    "Request failed";

  return { status, data, msg };
}

export default function ManagePaymentsPopup({
  isOpen,
  onClose,
  reservationGroupId,
  reservationId = null,
  reservationLocator = "",
  businessUnitId,
  currentUserId,
  canViewPayments = false,
  canEditNonStripePayments = false,
  canEditStripePayments = false,
}) {
  const { t: tr, locale, languageId, errorText } = useReservationModalTranslation();
  const { themeId } = usePmsTheme();
  const isReservationScope = reservationId != null;
  const scopedReservationId = isReservationScope ? Number(reservationId) : null;
  const {
    enabled: multiCurrencyEnabled,
    displayCurrency,
    baseCurrency,
    convertFromBase,
    currencyCatalog,
    formatFromBase,
    getMinorUnits,
  } = useCurrencyDisplay();
  const [contentReady, setContentReady] = useState(false);
  const [txTab, setTxTab] = useState("active"); // active | history

  const [activeMethod, setActiveMethod] = useState("stripe_card"); // stripe_card | manual_card | bank_transfer | cash
  const [selectedPmId, setSelectedPmId] = useState(null);
  const [amountEur, setAmountEur] = useState("");

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentIntents, setPaymentIntents] = useState([]); // keep for Stripe-only actions mapping
  const [groupPayments, setGroupPayments] = useState([]);   // unified ledger (Stripe + manual)
  const [paymentBalance, setPaymentBalance] = useState(null);

  const [refundAmountByIntentId, setRefundAmountByIntentId] = useState({});

  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [refundModal, setRefundModal] = useState({
    isOpen: false,
    payment_intent_id: null,
  });

  const [refundModalAmount, setRefundModalAmount] = useState("");

  const [captureModal, setCaptureModal] = useState({
    isOpen: false,
    payment_intent_id: null,
  });

  const [captureModalAmount, setCaptureModalAmount] = useState("");

  const [allocationModal, setAllocationModal] = useState({
    isOpen: false,
    kind: "payment",
    method: null,
    methodLabel: "",
    currency: null,
    amountCents: 0,
    rows: [],
    edited: false,
  });

  const groupId = Number(reservationGroupId);
  const loadSequence = useRef(0);
  const requestInFlight = useRef(false);
  const idempotencyFallbackRef = useRef(new Map());
  const idempotencyStorage = useMemo(() => {
    let primary = null;
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        primary = window.localStorage;
      }
    } catch (_error) {
      // Access itself can be blocked in browser privacy modes.
    }
    return createResilientStripeOperationStorage({
      primary,
      fallback: idempotencyFallbackRef.current,
    });
  }, []);
  const beginStripeOperation = useCallback(
    ({ action, intentId = null, amountCents = null }) => {
      const signature = buildStripeOperationSignature({
        userId: currentUserId,
        businessUnitId,
        groupId,
        reservationId: scopedReservationId,
        paymentMethodId: ["charge", "preauthorize"].includes(action) ? selectedPmId : null,
        action,
        intentId,
        amountCents,
      });
      return {
        signature,
        idempotencyKey: getOrCreateStripeOperationIdempotencyKey({
          signature,
          storage: idempotencyStorage,
        }),
      };
    },
    [businessUnitId, currentUserId, groupId, scopedReservationId, selectedPmId, idempotencyStorage]
  );
  const completeStripeOperation = useCallback(
    (signature) =>
      completeStripeOperationIdempotencyKey({
        signature,
        storage: idempotencyStorage,
      }),
    [idempotencyStorage]
  );
  const settleStripeOperationError = useCallback(
    (operation, responseData) => {
      if (
        operation?.signature &&
        stripeOperationKeyShouldRotate(responseData)
      ) {
        completeStripeOperation(operation.signature);
      }
    },
    [completeStripeOperation]
  );
  const canEditAnyPayments = canViewPayments && (canEditNonStripePayments || canEditStripePayments);
  const isReadOnlyMode = canViewPayments && !canEditAnyPayments;
  const manualCurrency = multiCurrencyEnabled ? displayCurrency : baseCurrency;
  const manualMinorUnits = typeof getMinorUnits === "function"
    ? getMinorUnits(manualCurrency)
    : getCurrencyMinorUnits(manualCurrency, currencyCatalog);
  const formatBaseCents = (cents) => formatFromBase(Number(cents || 0) / 100);
  const formatTenderCents = (cents, currencyCode = manualCurrency) => {
    const amount = Number(cents || 0) / 100;
    return formatCurrencyAmount(amount, currencyCode, {
      locale,
      catalog: currencyCatalog,
    });
  };
  const paymentBalanceInManualCurrency = useMemo(() => ({
    ...(paymentBalance || {}),
    reservations: (paymentBalance?.reservations || []).map((reservation) => {
      const convertReservationCents = (baseCents) => reservationBaseCentsToCurrencyCents({
        baseCents,
        snapshot: reservation.currency_snapshot,
        targetCurrency: manualCurrency,
        convertFromBase,
      });
      return {
        ...reservation,
        room_total: { cents: convertReservationCents(moneyShapeToCents(reservation.room_total)) },
        extras_total: { cents: convertReservationCents(moneyShapeToCents(reservation.extras_total)) },
        total: { cents: convertReservationCents(moneyShapeToCents(reservation.total)) },
        allocated_paid: { cents: convertReservationCents(moneyShapeToCents(reservation.allocated_paid)) },
        balance_due: { cents: convertReservationCents(moneyShapeToCents(reservation.balance_due)) },
      };
    }),
  }), [convertFromBase, manualCurrency, paymentBalance]);

  const defaultCard = useMemo(() => {
    return (paymentMethods || []).find((p) => !!p.is_default) || null;
  }, [paymentMethods]);

  const selectedPaymentMethod = useMemo(() => {
    const rows = Array.isArray(paymentMethods) ? paymentMethods : [];
    return rows.find((p) => Number(p?.id) === Number(selectedPmId)) || defaultCard || rows[0] || null;
  }, [defaultCard, paymentMethods, selectedPmId]);
  const hasSavedStripeCard = Boolean(selectedPaymentMethod);

  const stripePiByPaymentIntentId = useMemo(() => {
    const map = new Map();
    for (const pi of paymentIntents || []) {
      const k = String(pi.stripe_payment_intent_id || "").trim();
      if (!k) continue;
      map.set(k, pi);
    }
    return map;
  }, [paymentIntents]);

  const activeGroupPayments = useMemo(() => {
    const gps = Array.isArray(groupPayments) ? groupPayments : [];
    if (!gps.length) return [];

    return gps.filter((gp) => {
      const actionKey = String(gp.action || "").toLowerCase();
      const providerKey = String(gp.provider || "").toLowerCase();
      const methodKey = String(gp.method || "").toLowerCase();
      const providerRef = String(gp.provider_ref || "").trim();

      const isStripeRow = methodKey === "stripe_card" || providerKey === "stripe";

      // Active tab should show only "current" items. No actions on release/refund rows.
      if (actionKey === "refund" || actionKey === "release") return false;

      // Hide Stripe preauth once it was converted (captured) or released.
      if (isStripeRow && actionKey === "preauthorize") {
        const stripePi = providerRef ? (stripePiByPaymentIntentId.get(providerRef) || null) : null;
        if (!stripePi) return true; // can't infer state => keep visible

        const isManual = String(stripePi.capture_method || "").toLowerCase() === "manual";
        const isPreauthOpen =
          isManual &&
          !!stripePi.authorized_at &&
          !stripePi.captured_at &&
          !stripePi.canceled_at;

        return isPreauthOpen;
      }

      // Hide fully refunded final charges from Active
      if (isStripeRow && (actionKey === "collect" || actionKey === "capture")) {
        const stripePi = providerRef ? (stripePiByPaymentIntentId.get(providerRef) || null) : null;
        if (!stripePi) return true;

        const rowAmountCents = Number(gp.amount_cents || 0);
        const refunded = summarizeRefundEvents(stripePi.events).confirmedCents;

        if (rowAmountCents > 0 && refunded >= rowAmountCents) return false;
      }

      return true;
    });
  }, [groupPayments, stripePiByPaymentIntentId]);

  const scopedReservation = isReservationScope
    ? paymentBalance?.reservations?.find((row) => Number(row.reservation_id) === scopedReservationId)
    : null;
  const scopeReady = !isReservationScope || Boolean(scopedReservation);
  const scopeLabel = isReservationScope
    ? (scopedReservation?.locator || reservationLocator || tr("Reservation #{id}", { id: scopedReservationId }))
    : `${tr("Group #")}${groupId}`;
  const scopePayload = isReservationScope ? { reservation_id: scopedReservationId } : {};

  const balanceTotals = useMemo(() => {
    const totals = paymentBalance?.totals || {};
    return {
      gross: moneyShapeToCents(totals.gross_total),
      paid: moneyShapeToCents(totals.paid_net),
      due: moneyShapeToCents(totals.balance_due),
      unallocated: moneyShapeToCents(totals.unallocated_paid),
    };
  }, [paymentBalance]);


  const loadEverything = useCallback(async ({ silent = false } = {}) => {
    const sequence = ++loadSequence.current;
    if (!groupId || (isReservationScope && (!Number.isInteger(scopedReservationId) || scopedReservationId <= 0))) {
      setError("Missing reservation group id.");
      setPaymentMethods([]);
      setPaymentIntents([]);
      setGroupPayments([]);
      setPaymentBalance(null);
      return false;
    }

    if (!canViewPayments) {
      setError("You do not have permission to view payments for this reservation.");
      setNotice("");
      setPaymentMethods([]);
      setPaymentIntents([]);
      setGroupPayments([]);
      setPaymentBalance(null);
      return false;
    }

    if (!silent) setError("");
    requestInFlight.current = true;

    console.log("[PAYMENTS][POPUP][LOAD][START]", { groupId, businessUnitId });

    try {
      const [pmRes, piRes, gpRes, balanceOutcome] = await Promise.all([
        axios.get(`/api/stripe-transactions/groups/${groupId}/payment-methods`),
        isReservationScope
          ? axios.get(`/api/stripe-transactions/groups/${groupId}/payment-intents`, { params: { reservation_id: scopedReservationId } })
          : axios.get(`/api/stripe-transactions/groups/${groupId}/payment-intents`),
        isReservationScope
          ? axios.get(`/api/stripe-transactions/groups/${groupId}/group-payments`, { params: { reservation_id: scopedReservationId } })
          : axios.get(`/api/stripe-transactions/groups/${groupId}/group-payments`),
        axios.get(`/api/reservation-groups/${groupId}/payment-balance`)
          .then((res) => ({ ok: true, data: res.data }))
          .catch((err) => ({ ok: false, error: normalizeAxiosError(err) })),
      ]);

      const pms = Array.isArray(pmRes.data) ? pmRes.data : [];
      const intents = Array.isArray(piRes.data) ? piRes.data : [];
      const gps = Array.isArray(gpRes.data) ? gpRes.data : [];
      let balance = balanceOutcome.ok ? (balanceOutcome.data || null) : null;
      if (sequence !== loadSequence.current) return false;
      if (isReservationScope) {
        if ([piRes, gpRes].some((response) => Number(response.headers?.["x-pilcore-reservation-id"]) !== scopedReservationId)) {
          throw new Error("The server has not confirmed reservation-only payments. Please contact support before recording a payment.");
        }
        const reservation = balance?.reservations?.find((row) => Number(row.reservation_id) === scopedReservationId);
        if (!reservation) throw new Error("The selected reservation payment balance is unavailable. No payment can be recorded.");
        balance = {
          ...balance,
          reservations: [reservation],
          payment_status: reservation.payment_status,
          totals: {
            gross_total: reservation.total,
            paid_net: reservation.allocated_paid,
            balance_due: reservation.balance_due,
            unallocated_paid: { cents: 0 },
          },
        };
      }
      if (!balanceOutcome.ok) {
        console.warn("[PAYMENTS][POPUP][BALANCE][WARN]", balanceOutcome.error);
      }

      setPaymentMethods(pms);
      setPaymentIntents(isReservationScope ? intents.filter((row) => Number(row.reservation_id) === scopedReservationId) : intents);
      setGroupPayments(isReservationScope ? gps.filter((row) => Number(row.reservation_id) === scopedReservationId) : gps);
      setPaymentBalance(balance);
      setError("");

      const defaultRow = pms.find((p) => !!p.is_default) || pms[0] || null;
      setSelectedPmId((current) => pms.some((row) => Number(row.id) === Number(current))
        ? current : defaultRow ? Number(defaultRow.id) : null);
      setActiveMethod((currentMethod) => (
        currentMethod === "stripe_card" && !defaultRow ? "manual_card" : currentMethod
      ));

      console.log("[PAYMENTS][POPUP][LOAD][OK]", {
        groupId,
        paymentMethods: pms.length,
        paymentIntents: intents.length,
        groupPayments: gps.length,
        balanceLoaded: !!balance,
      });
      return true;

    } catch (err) {
      if (sequence !== loadSequence.current) return false;
      const norm = normalizeAxiosError(err);
      console.error("[PAYMENTS][POPUP][LOAD][ERR]", norm);

      setPaymentMethods([]);
      setPaymentIntents([]);
      setGroupPayments([]);
      setPaymentBalance(null);

      if (norm.status === 403) {
        setError("You do not have permission to view payments for this reservation.");
      } else {
        setError(norm.msg || "Failed to load payments data.");
      }
      return false;
    } finally {
      if (sequence === loadSequence.current) requestInFlight.current = false;
    }
  }, [groupId, businessUnitId, canViewPayments, isReservationScope, scopedReservationId]);

  useEffect(() => {
    if (!isOpen) return;

    setContentReady(false);
    setTxTab("active");
    setAmountEur("");
    setNotice("");
    setPaymentBalance(null);
    setPaymentIntents([]);
    setGroupPayments([]);
    setSelectedPmId(null);
    setActiveMethod("stripe_card");
    setAllocationModal((current) => ({ ...current, isOpen: false, rows: [] }));
    setCaptureModal({ isOpen: false, payment_intent_id: null });
    setRefundModal({ isOpen: false, payment_intent_id: null });
    let active = true;

    async function load() {
      await loadEverything();
      if (active) setContentReady(true);
    }

    load();
    return () => { active = false; loadSequence.current += 1; requestInFlight.current = false; };
  }, [isOpen, loadEverything]);

  useEffect(() => {
    if (!isOpen || !contentReady || isWorking) return undefined;
    const refresh = () => {
      if (document.visibilityState === "hidden" || requestInFlight.current) return;
      void loadEverything({ silent: true });
    };
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [isOpen, contentReady, isWorking, loadEverything]);

  if (!isOpen) return null;
  if (!contentReady) return null;

  const stripeEnabled = activeMethod === "stripe_card";
  const canCreateWithSelectedMethod = scopeReady && canViewPayments && (stripeEnabled
    ? canEditStripePayments && hasSavedStripeCard
    : canEditNonStripePayments);
  const txRows =
    txTab === "active"
      ? activeGroupPayments
      : (Array.isArray(groupPayments) ? groupPayments : []);
  const allocationCurrency = allocationModal.currency || manualCurrency;
  const allocationMinorUnits = typeof getMinorUnits === "function"
    ? getMinorUnits(allocationCurrency)
    : getCurrencyMinorUnits(allocationCurrency, currencyCatalog);
  const allocationAllocatedCents = (allocationModal.rows || []).reduce((sum, row) => {
    if (!hasValidCurrencyPrecision(row.inputValue, allocationCurrency, currencyCatalog)) return sum;
    return sum + Math.max(0, eurToCents(row.inputValue) || 0);
  }, 0);
  const allocationUnallocatedCents = Math.max(
    0,
    Number(allocationModal.amountCents || 0) - allocationAllocatedCents
  );
  const allocationOverCents = Math.max(
    0,
    allocationAllocatedCents - Number(allocationModal.amountCents || 0)
  );
  const isAllocationRefund = allocationModal.kind === "refund";

  function requireValidAmountOrThrow({ currencyCode = "EUR", validatePrecision = false } = {}) {
    if (!scopeReady) throw new Error("The selected reservation payment balance is unavailable. No payment can be recorded.");
    const n = Number(String(amountEur).replace(",", "."));
    if (!Number.isFinite(n) || n <= 0) {
      const e = new Error("Invalid amount. Please enter a positive amount.");
      e.code = "INVALID_AMOUNT";
      throw e;
    }
    if (
      validatePrecision &&
      !hasValidCurrencyPrecision(amountEur, currencyCode, currencyCatalog)
    ) {
      const e = new Error(
        `${currencyCode} accepts ${getCurrencyMinorUnits(currencyCode, currencyCatalog)} decimal places.`
      );
      e.code = "INVALID_CURRENCY_PRECISION";
      throw e;
    }
    return n;
  }

  function buildScopedAllocationRows(amountCents, isRefund = false) {
    const row = paymentBalanceInManualCurrency.reservations.find(
      (reservation) => Number(reservation.reservation_id) === scopedReservationId
    );
    if (!row) throw new Error("The selected reservation payment balance is unavailable. No payment can be recorded.");
    const paidCents = Math.max(0, moneyShapeToCents(row.allocated_paid));
    if (isRefund && amountCents > paidCents) throw new Error("The refund exceeds the amount paid for this reservation.");
    return [{
      reservation_id: scopedReservationId,
      label: row.locator || reservationLocator,
      group_index: row.group_index,
      total_cents: moneyShapeToCents(row.total),
      allocated_cents: paidCents,
      due_cents: moneyShapeToCents(row.balance_due),
      suggested_cents: amountCents,
    }];
  }

  function canModifyScopedIntent(intentRowId) {
    if (!canViewPayments || !scopeReady) return false;
    if (!isReservationScope) return true;
    return paymentIntents.some((intent) => Number(intent.id) === Number(intentRowId)
      && Number(intent.reservation_id) === scopedReservationId);
  }

  async function doPreauthorize() {
    let operation = null;
    try {
      if (!canEditStripePayments) {
        window.alert(tr("You do not have permission to edit Stripe payments."));
        return;
      }

      if (!stripeEnabled) return;

      if (!hasSavedStripeCard) {
        const msg = "No saved card found for this group.";
        setError(msg);
        window.alert(errorText(msg));
        return;
      }

      const amt = requireValidAmountOrThrow();

      if (!window.confirm(tr("Authorize {amount} on the selected Stripe card for {scope}?", { amount: formatTenderCents(eurToCents(amt), "EUR"), scope: scopeLabel }))) return;
      operation = beginStripeOperation({
        action: "preauthorize",
        amountCents: eurToCents(amt),
      });

      setIsWorking(true);
      setError("");
      setNotice("");

      console.log("[PAYMENTS][POPUP][STRIPE][PREAUTH][CLICK]", { groupId, amountEur: amt });

      const res = await axios.post(
        `/api/stripe-transactions/groups/${groupId}/preauthorize`,
        { amount_eur: amt, payment_method_id: Number(selectedPaymentMethod.id), ...scopePayload },
        stripeIdempotencyRequestConfig(operation.idempotencyKey)
      );
      completeStripeOperation(operation.signature);

      console.log("[PAYMENTS][POPUP][STRIPE][PREAUTH][OK]", res.data);

      const refreshed = await loadEverything();
      setNotice(
        stripeOperationSuccessNotice({
          responseData: res.data,
          refreshed,
          successMessage: "Authorization created successfully.",
          recoveredMessage:
            "Recovered the previous authorization; no new authorization was created.",
          refreshFailedMessage:
            "Authorization succeeded, but the latest payment data could not be refreshed. Reopen this panel to reload it.",
        })
      );
    } catch (err) {
      const norm = normalizeAxiosError(err);
      settleStripeOperationError(operation, norm.data);
      console.error("[PAYMENTS][POPUP][STRIPE][PREAUTH][ERR]", norm);

      setError(norm.msg || "Authorization failed.");
      window.alert(errorText(norm.msg || "Authorization failed.", "Authorization failed."));
    } finally {
      setIsWorking(false);
    }
  }

  async function doCollect() {
    let operation = null;
    try {
      if (!canEditStripePayments) {
        window.alert(tr("You do not have permission to edit Stripe payments."));
        return;
      }

      if (!stripeEnabled) return;

      if (!hasSavedStripeCard) {
        const msg = "No saved card found for this group.";
        setError(msg);
        window.alert(errorText(msg));
        return;
      }

      const amt = requireValidAmountOrThrow();

      if (!window.confirm(tr("Collect {amount} on the selected Stripe card for {scope}?", { amount: formatTenderCents(eurToCents(amt), "EUR"), scope: scopeLabel }))) return;
      operation = beginStripeOperation({
        action: "charge",
        amountCents: eurToCents(amt),
      });

      setIsWorking(true);
      setError("");
      setNotice("");

      console.log("[PAYMENTS][POPUP][STRIPE][CHARGE][CLICK]", { groupId, amountEur: amt });

      const res = await axios.post(
        `/api/stripe-transactions/groups/${groupId}/charge`,
        { amount_eur: amt, payment_method_id: Number(selectedPaymentMethod.id), ...scopePayload },
        stripeIdempotencyRequestConfig(operation.idempotencyKey)
      );
      completeStripeOperation(operation.signature);

      console.log("[PAYMENTS][POPUP][STRIPE][CHARGE][OK]", res.data);

      const refreshed = await loadEverything();
      setNotice(
        stripeOperationSuccessNotice({
          responseData: res.data,
          refreshed,
          successMessage: "Payment collected successfully.",
          recoveredMessage:
            "Recovered the previous payment; no new payment was collected.",
          refreshFailedMessage:
            "Payment succeeded, but the latest payment data could not be refreshed. Reopen this panel to reload it.",
        })
      );
    } catch (err) {
      const norm = normalizeAxiosError(err);
      settleStripeOperationError(operation, norm.data);
      console.error("[PAYMENTS][POPUP][STRIPE][CHARGE][ERR]", norm);

      setError(norm.msg || "Collect failed.");
      window.alert(errorText(norm.msg || "Collect failed.", "Collect failed."));
    } finally {
      setIsWorking(false);
    }
  }

  async function doRecordManualPayment() {
    try {
      if (!canEditNonStripePayments) {
        window.alert(tr("You do not have permission to edit non-Stripe payments."));
        return;
      }

      if (stripeEnabled) return;

      const method = String(activeMethod || "").trim().toLowerCase();
      if (!["manual_card", "bank_transfer", "cash"].includes(method)) {
        window.alert(tr("Select a non-Stripe payment method first."));
        return;
      }

      const amt = requireValidAmountOrThrow({
        currencyCode: manualCurrency,
        validatePrecision: true,
      });
      const methodLabel =
        method === "manual_card"
          ? "External card"
          : method === "bank_transfer"
          ? "Bank Transfer"
          : "Cash";

      setError("");
      setNotice("");

      const amountCents = eurToCents(amt);
      const rows = (isReservationScope
        ? buildScopedAllocationRows(amountCents)
        : buildSuggestedAllocations(paymentBalanceInManualCurrency, amountCents)).map((row) => ({
        ...row,
        inputValue: centsToCurrencyInput(row.suggested_cents, manualCurrency, currencyCatalog),
      }));

      console.log("[PAYMENTS][POPUP][MANUAL][ALLOCATION][OPEN]", {
        groupId,
        method,
        amountCents,
        rows: rows.length,
      });

      setAllocationModal({
        isOpen: true,
        kind: "payment",
        method,
        methodLabel,
        currency: manualCurrency,
        amountCents,
        rows,
        edited: false,
      });
    } catch (err) {
      const norm = normalizeAxiosError(err);
      console.error("[PAYMENTS][POPUP][MANUAL][ALLOCATE][ERR]", norm);
      setError(norm.msg || "Manual payment failed.");
      window.alert(errorText(norm.msg || "Manual payment failed.", "Manual payment failed."));
    }
  }

  async function doRecordManualRefund() {
    try {
      if (!canEditNonStripePayments) {
        window.alert(tr("You do not have permission to edit non-Stripe payments."));
        return;
      }

      if (stripeEnabled) return;

      const method = String(activeMethod || "").trim().toLowerCase();
      if (!["manual_card", "bank_transfer", "cash"].includes(method)) {
        window.alert(tr("Select a non-Stripe refund method first."));
        return;
      }

      const amt = requireValidAmountOrThrow({
        currencyCode: manualCurrency,
        validatePrecision: true,
      });
      const methodLabel =
        method === "manual_card"
          ? "External card"
          : method === "bank_transfer"
          ? "Bank Transfer"
          : "Cash";

      setError("");
      setNotice("");

      const amountCents = eurToCents(amt);
      const rows = (isReservationScope
        ? buildScopedAllocationRows(amountCents, true)
        : buildSuggestedRefundAllocations(paymentBalanceInManualCurrency, amountCents)).map((row) => ({
        ...row,
        inputValue: centsToCurrencyInput(row.suggested_cents, manualCurrency, currencyCatalog),
      }));

      console.log("[PAYMENTS][POPUP][MANUAL_REFUND][ALLOCATION][OPEN]", {
        groupId,
        method,
        amountCents,
        rows: rows.length,
      });

      setAllocationModal({
        isOpen: true,
        kind: "refund",
        method,
        methodLabel,
        currency: manualCurrency,
        amountCents,
        rows,
        edited: false,
      });
    } catch (err) {
      const norm = normalizeAxiosError(err);
      console.error("[PAYMENTS][POPUP][MANUAL_REFUND][ALLOCATE][ERR]", norm);
      setError(norm.msg || "Manual refund failed.");
      window.alert(errorText(norm.msg || "Manual refund failed.", "Manual refund failed."));
    }
  }

  function updateAllocationInput(reservationId, rawValue) {
    if (isReservationScope) return;
    const cleaned = String(rawValue || "").replace(",", ".").replace(/[^0-9.]/g, "");

    setAllocationModal((prev) => ({
      ...prev,
      edited: true,
      rows: (prev.rows || []).map((row) => (
        Number(row.reservation_id) === Number(reservationId)
          ? { ...row, inputValue: cleaned }
          : row
      )),
    }));
  }

  async function submitManualPaymentWithAllocations({ leaveUnallocated = false } = {}) {
    try {
      if (!canViewPayments || !canEditNonStripePayments || !scopeReady) return;
      const modal = allocationModal || {};
      const isRefund = modal.kind === "refund";
      const tenderCurrency = modal.currency || manualCurrency;
      const amountCents = Number(modal.amountCents || 0);
      if (!amountCents || amountCents <= 0 || !modal.method) {
        window.alert(isRefund ? tr("Invalid refund allocation.") : tr("Invalid payment allocation."));
        return;
      }

      const allocationRows = leaveUnallocated
        ? []
        : (modal.rows || [])
            .map((row) => ({
              reservation_id: Number(row.reservation_id),
              amount_cents: eurToCents(row.inputValue) || 0,
            }))
            .filter((row) => row.reservation_id && row.amount_cents > 0);

      if (isReservationScope && (leaveUnallocated || allocationRows.length !== 1
        || allocationRows[0].reservation_id !== scopedReservationId
        || allocationRows[0].amount_cents !== amountCents)) {
        throw new Error("The full amount must apply only to the selected reservation.");
      }

      const invalidPrecisionRow = !leaveUnallocated && (modal.rows || []).find(
        (row) =>
          String(row.inputValue || "").trim() &&
          !hasValidCurrencyPrecision(row.inputValue, tenderCurrency, currencyCatalog)
      );
      if (invalidPrecisionRow) {
        window.alert(
          tr("{currency} accepts {decimals} decimal places.", {
            currency: tenderCurrency,
            decimals: getCurrencyMinorUnits(tenderCurrency, currencyCatalog),
          })
        );
        return;
      }

      const allocatedCents = allocationRows.reduce(
        (sum, row) => sum + Number(row.amount_cents || 0),
        0
      );

      if (allocatedCents > amountCents) {
        window.alert(
          isRefund
            ? tr("Allocated amount cannot be greater than refund amount.")
            : tr("Allocated amount cannot be greater than payment amount.")
        );
        return;
      }

      setIsWorking(true);
      setError("");
      setNotice("");

      console.log("[PAYMENTS][POPUP][MANUAL][CREATE][CLICK]", {
        groupId,
        kind: modal.kind,
        method: modal.method,
        amountCents,
        leaveUnallocated,
        allocationRows: allocationRows.length,
      });

      const res = await axios.post(
        `/api/stripe-transactions/groups/${groupId}/${isRefund ? "manual-refund" : "manual-payment"}`,
        {
          amount: centsToCurrencyInput(amountCents, tenderCurrency, currencyCatalog),
          currency: tenderCurrency,
          method: modal.method,
          business_unit_id: businessUnitId,
          ...scopePayload,
          allocation_mode: leaveUnallocated
            ? "unallocated"
            : isReservationScope || modal.edited
            ? "manual"
            : "auto",
          allocations: allocationRows,
        }
      );

      console.log("[PAYMENTS][POPUP][MANUAL][CREATE][OK]", res.data);

      setNotice(isRefund ? "Manual refund recorded successfully." : "Manual payment recorded successfully.");
      setAmountEur("");
      setAllocationModal({
        isOpen: false,
        kind: "payment",
        method: null,
        methodLabel: "",
        currency: null,
        amountCents: 0,
        rows: [],
        edited: false,
      });
      await loadEverything();
    } catch (err) {
      const norm = normalizeAxiosError(err);
      console.error("[PAYMENTS][POPUP][MANUAL][CREATE][ERR]", norm);
      const isRefund = allocationModal?.kind === "refund";
      setError(norm.msg || (isRefund ? "Manual refund failed." : "Manual payment failed."));
      window.alert(errorText(norm.msg || (isRefund ? "Manual refund failed." : "Manual payment failed.")));
    } finally {
      setIsWorking(false);
    }
  }

  async function doCapture(intentRowId, amountEurOrNull) {
    let operation = null;
    try {
      if (!canEditStripePayments) {
        window.alert(tr("You do not have permission to edit Stripe payments."));
        return;
      }

      if (!intentRowId) return;

      if (!canModifyScopedIntent(intentRowId)) return;
      const payload = { ...scopePayload };
      if (amountEurOrNull != null) payload.amount_eur = amountEurOrNull;
      operation = beginStripeOperation({
        action: "capture",
        intentId: intentRowId,
        amountCents:
          amountEurOrNull == null ? null : eurToCents(amountEurOrNull),
      });

      setIsWorking(true);
      setError("");
      setNotice("");

      console.log("[PAYMENTS][POPUP][STRIPE][CAPTURE][START]", {
        intentRowId,
        payload,
        stripe_payment_intent_id: paymentIntents?.find((x) => Number(x.id) === Number(intentRowId))?.stripe_payment_intent_id || null,
      });

      const res = await axios.post(
        `/api/stripe-transactions/payment-intents/${intentRowId}/capture`,
        payload,
        stripeIdempotencyRequestConfig(operation.idempotencyKey)
      );
      completeStripeOperation(operation.signature);

      console.log("[PAYMENTS][POPUP][STRIPE][CAPTURE][OK]", res.data);

      const refreshed = await loadEverything();
      setNotice(
        stripeOperationSuccessNotice({
          responseData: res.data,
          refreshed,
          successMessage: "Authorization captured successfully.",
          recoveredMessage:
            "Recovered the previous capture; no new capture was created.",
          refreshFailedMessage:
            "Capture succeeded, but the latest payment data could not be refreshed. Reopen this panel to reload it.",
        })
      );
    } catch (err) {
      const norm = normalizeAxiosError(err);
      settleStripeOperationError(operation, norm.data);
      console.error("[PAYMENTS][POPUP][STRIPE][CAPTURE][ERR]", {
        ...norm,
        intentRowId,
        stripe_payment_intent_id: paymentIntents?.find((x) => Number(x.id) === Number(intentRowId))?.stripe_payment_intent_id || null,
      });

      setError(norm.msg || "Capture failed.");
      window.alert(errorText(norm.msg || "Capture failed.", "Capture failed."));
    } finally {
      setIsWorking(false);
    }
  }

  async function doRelease(intentRowId) {
    let operation = null;
    try {
      if (!canEditStripePayments) {
        window.alert(tr("You do not have permission to edit Stripe payments."));
        return;
      }

      if (!intentRowId) return;
      if (!canModifyScopedIntent(intentRowId)) return;

      if (!window.confirm(tr("Release this authorization?"))) return;
      operation = beginStripeOperation({
        action: "release",
        intentId: intentRowId,
      });

      setIsWorking(true);
      setError("");
      setNotice("");

      console.log("[PAYMENTS][POPUP][STRIPE][RELEASE][CLICK]", { intentRowId });

      const res = await axios.post(
        `/api/stripe-transactions/payment-intents/${intentRowId}/release`,
        { ...scopePayload },
        stripeIdempotencyRequestConfig(operation.idempotencyKey)
      );
      completeStripeOperation(operation.signature);

      console.log("[PAYMENTS][POPUP][STRIPE][RELEASE][OK]", res.data);

      const refreshed = await loadEverything();
      setNotice(
        stripeOperationSuccessNotice({
          responseData: res.data,
          refreshed,
          successMessage: "Authorization released successfully.",
          recoveredMessage:
            "Recovered the previous release; no new release was created.",
          refreshFailedMessage:
            "Release succeeded, but the latest payment data could not be refreshed. Reopen this panel to reload it.",
        })
      );
    } catch (err) {
      const norm = normalizeAxiosError(err);
      settleStripeOperationError(operation, norm.data);
      console.error("[PAYMENTS][POPUP][STRIPE][RELEASE][ERR]", norm);

      setError(norm.msg || "Release failed.");
      window.alert(errorText(norm.msg || "Release failed.", "Release failed."));
    } finally {
      setIsWorking(false);
    }
  }

  async function doRefund(intentRowId, amountEurOrNull) {
    let operation = null;
    try {
      if (!canEditStripePayments) {
        window.alert(tr("You do not have permission to edit Stripe payments."));
        return;
      }

      if (!intentRowId) return;

      if (!canModifyScopedIntent(intentRowId)) return;
      const payload = { ...scopePayload };
      if (amountEurOrNull != null) payload.amount_eur = amountEurOrNull;
      operation = beginStripeOperation({
        action: "refund",
        intentId: intentRowId,
        amountCents:
          amountEurOrNull == null ? null : eurToCents(amountEurOrNull),
      });

      setIsWorking(true);
      setError("");
      setNotice("");

      console.log("[PAYMENTS][POPUP][STRIPE][REFUND][START]", {
        intentRowId,
        payload,
        // Helpful context for debugging mapping (DB id vs Stripe PI id)
        stripe_payment_intent_id: paymentIntents?.find((x) => Number(x.id) === Number(intentRowId))?.stripe_payment_intent_id || null,
        has_paymentIntents: Array.isArray(paymentIntents) ? paymentIntents.length : 0,
      });

      const res = await axios.post(
        `/api/stripe-transactions/payment-intents/${intentRowId}/refund`,
        payload,
        stripeIdempotencyRequestConfig(operation.idempotencyKey)
      );
      completeStripeOperation(operation.signature);

      console.log("[PAYMENTS][POPUP][STRIPE][REFUND][OK]", res.data);

      const refreshed = await loadEverything();
      setNotice(
        stripeOperationSuccessNotice({
          responseData: res.data,
          refreshed,
          successMessage: "Refund created successfully.",
          recoveredMessage:
            "Recovered the previous refund; no new refund was created.",
          refreshFailedMessage:
            "Refund succeeded, but the latest payment data could not be refreshed. Reopen this panel to reload it.",
        })
      );
    } catch (err) {
      const norm = normalizeAxiosError(err);
      settleStripeOperationError(operation, norm.data);
      console.error("[PAYMENTS][POPUP][STRIPE][REFUND][ERR]", {
        ...norm,
        intentRowId,
        // Keep context in error too
        stripe_payment_intent_id: paymentIntents?.find((x) => Number(x.id) === Number(intentRowId))?.stripe_payment_intent_id || null,
      });

      setError(norm.msg || "Refund failed.");
      window.alert(errorText(norm.msg || "Refund failed.", "Refund failed."));
    } finally {
      setIsWorking(false);
    }
  }

  const popupContent = (
    <>
      <div className={`mp-popup-overlay mp-theme-${themeId === "light" ? "light" : "dark"}`}>
        <div className="mp-popup-shell" role="dialog" aria-modal="true" aria-labelledby="mp-payments-title">
          <div className="mp-popup-body">
            <div className="mp-popup-header">
            <div>
              <h2 id="mp-payments-title" className="mp-popup-title">{tr(isReservationScope ? "Manage Reservation Payments" : "Manage Group Payments")}</h2>
              <div className="mp-popup-subtitle">{scopeLabel} {tr("· Business Unit")} {businessUnitId || "-"}
              </div>
              <div className="mp-scopeNotice">{tr(isReservationScope
                ? "Payments and refunds apply only to this reservation. Other reservations are not affected."
                : "Group payments can be distributed across its reservations.")}</div>
              {isReadOnlyMode && !error ? (
                <div className="text-warning" style={{ fontSize: "0.85rem", marginTop: 4 }}> {tr("Read-only mode: you can view transactions but cannot create or edit payments.")} </div>
              ) : null}
            </div>

            <div className="mp-popup-actions">
              <button
                type="button"
                className="btn btn-sm btn-outline-light fw-bold px-3"
                disabled={isWorking}
                onClick={() => {
                  console.log("[PAYMENTS][POPUP][CLOSE][CLICK]", { groupId });
                  onClose();
                }}
              >
                <FiX size={15} /> {tr("Close")} </button>
            </div>
          </div>

          {error ? (
            <div className="alert alert-danger py-2 mb-3">{errorText(error)}</div>
          ) : null}

          {notice ? (
            <div className="alert alert-success py-2 mb-3">{formatReservationPaymentNotice(notice, languageId)}</div>
          ) : null}

          {paymentBalance ? (
            <div className="mp-panel">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <div className="mp-panel-title">{tr("Payment Balance")}</div>
                <div className="mp-panel-subtitle">
                  {translateReservationStatus(paymentBalance.payment_status, languageId) || "-"}
                </div>
              </div>

              <div className={`mp-balanceGrid${isReservationScope ? " mp-balanceGrid--reservation" : ""}`}>
                <div className="mp-balanceMetric">
                  <div className="mp-balanceLabel">{tr("Total")}</div>
                  <div className="mp-balanceValue">{formatBaseCents(balanceTotals.gross)}</div>
                </div>
                <div className="mp-balanceMetric">
                  <div className="mp-balanceLabel">{tr("Paid")}</div>
                  <div className="mp-balanceValue">{formatBaseCents(balanceTotals.paid)}</div>
                </div>
                <div className="mp-balanceMetric">
                  <div className="mp-balanceLabel">{tr("Pending")}</div>
                  <div className="mp-balanceValue">{formatBaseCents(balanceTotals.due)}</div>
                </div>
                {!isReservationScope ? <div className="mp-balanceMetric">
                  <div className="mp-balanceLabel">{tr("Unallocated")}</div>
                  <div className="mp-balanceValue">{formatBaseCents(balanceTotals.unallocated)}</div>
                </div> : null}
              </div>

              {Array.isArray(paymentBalance.reservations) && paymentBalance.reservations.length ? (
                <div className="table-responsive mp-table-wrapper mt-3" style={{ maxHeight: "260px" }}>
                  <table className="table table-dark table-striped table-sm align-middle mb-0 mp-popup-table">
                    <thead>
                      <tr>
                        <th>{tr("Reservation")}</th>
                        <th style={{ width: "130px" }}>{tr("Room charges")}</th>
                        <th style={{ width: "130px" }}>{tr("Extras")}</th>
                        <th style={{ width: "130px" }}>{tr("Total")}</th>
                        <th style={{ width: "130px" }}>{tr("Assigned")}</th>
                        <th style={{ width: "130px" }}>{tr("Pending")}</th>
                        <th style={{ width: "150px" }}>{tr("Status")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentBalance.reservations.map((reservation) => (
                        <tr key={reservation.reservation_id}>
                          <td>
                            <div className="fw-bold">
                              {reservation.locator || tr("Reservation #{id}", { id: reservation.reservation_id })}
                            </div>
                            {reservation.room_number ? <div className="text-secondary" style={{ fontSize: "0.78rem" }}>{tr("Room")} {reservation.room_number}</div> : null}
                          </td>
                          <td>{formatBaseCents(moneyShapeToCents(reservation.room_total))}</td>
                          <td>{formatBaseCents(moneyShapeToCents(reservation.extras_total))}</td>
                          <td>{formatBaseCents(moneyShapeToCents(reservation.total))}</td>
                          <td>{formatBaseCents(moneyShapeToCents(reservation.allocated_paid))}</td>
                          <td>{formatBaseCents(moneyShapeToCents(reservation.balance_due))}</td>
                          <td>{translateReservationStatus(reservation.payment_status, languageId) || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="mp-panel">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <div className="mp-panel-title">{tr("New Transaction")}</div>
            </div>


            <div className="mp-newTransactionGrid">
            {/* LEFT: method + amount + actions */}
            <div className="mp-formColumn">
                <label className="form-label mb-1" style={{ fontSize: "0.85rem" }}> {tr("Method")} </label>

                <div className="mp-methodTabs" role="group" aria-label={tr("Method")}>
                  {[
                    ["stripe_card", "Saved card · Stripe"],
                    ["manual_card", "External card"],
                    ["bank_transfer", "Bank Transfer"],
                    ["cash", "Cash"],
                  ].map(([method, label]) => (
                    <button
                      key={method}
                      type="button"
                      className="btn btn-sm fw-bold px-3 mp-methodButton"
                      aria-pressed={activeMethod === method}
                      disabled={isWorking || (method === "stripe_card" && !hasSavedStripeCard)}
                      title={method === "stripe_card" && !hasSavedStripeCard ? tr("No saved card found for this group.") : ""}
                      onClick={() => setActiveMethod(method)}
                    >
                      {activeMethod === method ? <FiCheckCircle aria-hidden="true" /> : null}
                      {tr(label)}
                    </button>
                  ))}
                </div>
                <p className="mp-panel-note">{tr(stripeEnabled
                  ? "Charges the selected saved card through the Stripe payment gateway."
                  : activeMethod === "manual_card"
                    ? "Records a card payment already made on an external terminal. No charge is sent to Stripe."
                    : "Records a payment received outside the payment gateway.")}</p>

                <div className="mp-amountActions">
                <div style={{ width: "360px", maxWidth: "100%" }}>
                    <label htmlFor="mp-operation-amount" className="form-label mb-1" style={{ fontSize: "0.85rem" }}> {tr("Amount (")}{stripeEnabled ? "EUR" : manualCurrency})
                    </label>
                    <input
                    id="mp-operation-amount"
                    type={stripeEnabled ? "text" : "number"}
                    inputMode={stripeEnabled || manualMinorUnits > 0 ? "decimal" : "numeric"}
                    step={stripeEnabled ? undefined : currencyAmountStep(manualCurrency, currencyCatalog)}
                    className="form-control form-control-sm"
                    value={amountEur}
                    onChange={(e) => {
                        const raw = String(e.target.value || "");
                        const cleaned = raw.replace(",", ".").replace(/[^0-9.]/g, "");
                        setAmountEur(cleaned);
                    }}
                    disabled={isWorking || !canCreateWithSelectedMethod}
                    placeholder={stripeEnabled ? "0.00" : centsToCurrencyInput(0, manualCurrency, currencyCatalog)}
                    />
                </div>

                <div className="d-flex flex-wrap gap-2">
                    <button
                    type="button"
                    className="btn btn-sm btn-outline-warning fw-bold px-3"
                    disabled={isWorking || !scopeReady || !stripeEnabled || !canEditStripePayments || !hasSavedStripeCard}
                    onClick={doPreauthorize}
                    title={
                      !stripeEnabled
                        ? tr("Authorize is only available for Stripe card payments.")
                        : !hasSavedStripeCard
                        ? tr("No saved card found for this group.")
                        : !canEditStripePayments
                        ? tr("You do not have permission to edit Stripe payments.")
                        : ""
                    }
                    > {tr("Authorize")} </button>

                    <button
                    type="button"
                    className="btn btn-sm btn-outline-success fw-bold px-3"
                    disabled={isWorking || !canCreateWithSelectedMethod}
                    onClick={stripeEnabled ? doCollect : doRecordManualPayment}
                    title={
                      stripeEnabled
                        ? !hasSavedStripeCard
                          ? tr("No saved card found for this group.")
                          : !canEditStripePayments
                          ? tr("You do not have permission to edit Stripe payments.")
                          : ""
                        : !canEditNonStripePayments
                        ? tr("You do not have permission to edit non-Stripe payments.")
                        : ""
                    }
                    >
                    {stripeEnabled ? tr("Collect") : tr("Record")}
                    </button>

                    {!stripeEnabled ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger fw-bold px-3"
                        disabled={isWorking || !canViewPayments || !scopeReady || !canEditNonStripePayments}
                        onClick={doRecordManualRefund}
                        title={
                          !canEditNonStripePayments
                            ? tr("You do not have permission to edit non-Stripe payments.")
                            : ""
                        }
                      > {tr("Refund")} </button>
                    ) : null}
                </div>
                </div>
            </div>

            {/* RIGHT: saved card information */}
            <div className="mp-cardPanel">
                <div className="mp-cardPanelTitle">
                <FiCreditCard size={15} /> {tr("Saved Card")} </div>

                {activeMethod === "stripe_card" && selectedPaymentMethod ? (
                  <div className="mp-cardHero">
                    <div className="mp-cardHeroTop">
                      <div>
                        <div className="mp-cardBrand">
                          {(selectedPaymentMethod.brand || tr("Card")).toString().toUpperCase()}
                        </div>
                        <div className="mp-cardDigits">
                          **** {selectedPaymentMethod.last4 || "----"}
                        </div>
                      </div>
                      {selectedPaymentMethod.is_default ? (
                        <span className="mp-cardChip mp-cardChip--ok">
                          <FiCheckCircle size={13} /> {tr("Default")} </span>
                      ) : null}
                    </div>
                    <div className="mp-cardMeta">
                      <span className="mp-cardChip"> {tr("Exp")} {selectedPaymentMethod.exp_month || "--"}/{selectedPaymentMethod.exp_year || "----"}
                      </span>
                      {selectedPaymentMethod.cardholder_name ? (
                        <span className="mp-cardChip">{selectedPaymentMethod.cardholder_name}</span>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {activeMethod === "stripe_card" ? (
                <div className="d-flex flex-column gap-2">
                    {(paymentMethods || []).length ? (
                    (paymentMethods || []).map((pm) => {
                        const isSelected = Number(selectedPmId) === Number(pm.id);

                        return (
                        <button
                            key={pm.id}
                            type="button"
                            className={`btn btn-sm fw-bold text-start mp-cardOption ${
                            isSelected ? "btn-outline-light" : "btn-outline-secondary"
                            }`}
                            disabled={isWorking}
                            aria-pressed={isSelected}
                            onClick={() => {
                            console.log("[PAYMENTS][POPUP][CARD][SELECT]", { pmId: pm.id });
                            setSelectedPmId(Number(pm.id));
                            }}
                        >
                            <div>
                            {(pm.brand || tr("Card")).toUpperCase()} •••• {pm.last4 || "----"}
                            </div>
                            <div style={{ fontSize: "0.85rem", opacity: 0.85 }}> {tr("exp")} {(pm.exp_month || "--")}/{(pm.exp_year || "----")}
                            {pm.is_default ? ` · ${tr("Default")}` : ""}
                            </div>
                        </button>
                        );
                    })
                    ) : (
                    <div className="mp-emptyCard"> {tr("No saved cards found for this group.")} </div>
                    )}

                </div>
                ) : (
                <div className="mp-emptyCard">
                    {hasSavedStripeCard
                      ? tr("Select Saved card · Stripe to use the payment gateway.")
                      : tr("No saved cards found for this group.")}
                </div>
                )}
            </div>
            </div>

                        

          </div>
          {/* TRANSACTIONS (User-friendly) */}
          <div className="mp-panel">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
              <div className="mp-panel-title">{tr("Transactions")}</div>
              <div className="mp-tabs" role="tablist" aria-label={tr("Transactions")}>
                <button
                  type="button"
                  className={`btn btn-sm fw-bold px-3 mp-tab ${
                    txTab === "active" ? "mp-tab--active" : ""
                  }`}
                  disabled={isWorking}
                  role="tab"
                  aria-selected={txTab === "active"}
                  onClick={() => setTxTab("active")}
                > {tr("Active")} </button>
                <button
                  type="button"
                  className={`btn btn-sm fw-bold px-3 mp-tab ${
                    txTab === "history" ? "mp-tab--active" : ""
                  }`}
                  disabled={isWorking}
                  role="tab"
                  aria-selected={txTab === "history"}
                  onClick={() => setTxTab("history")}
                > {tr("History")} </button>
              </div>
            </div>

            <div className="mp-panel-subtitle">
              {txTab === "active"
                ? tr("Active transactions (open authorizations + final charges).")
                : tr("Read-only history (all payment movements).")}
            </div>

            <div
              className="table-responsive mp-table-wrapper"
              style={{
                maxHeight: "420px",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <table
                className="table table-dark table-striped table-sm align-middle mb-0 mp-popup-table"
                style={{
                  fontSize: "0.85rem",
                  lineHeight: "1.15rem",
                  minWidth: "880px",
                  whiteSpace: "nowrap",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: "170px" }}>{tr("Date")}</th>
                    <th style={{ width: "160px" }}>{tr("Method")}</th>
                    <th style={{ width: "170px" }}>{tr("Type")}</th>
                    <th style={{ width: "140px" }}>{tr("Amount")}</th>
                    <th style={{ width: "160px" }}>{tr("Status")}</th>
                    <th>{txTab === "history" ? tr("Description") : tr("Actions")}</th>
                  </tr>
                </thead>

                <tbody>
                  {(txRows || []).length ? (
                    (txRows || []).map((gp) => {
                      const methodKey = String(gp.method || "").toLowerCase();
                      const actionKey = String(gp.action || "").toLowerCase();
                      const providerKey = String(gp.provider || "").toLowerCase();
                      const providerRef = String(gp.provider_ref || "").trim();
                      const isStripeRow = methodKey === "stripe_card" || providerKey === "stripe";

                      const methodTxt =
                        methodKey === "stripe_card" ? tr("Saved card · Stripe") :
                        methodKey === "manual_card" || methodKey === "direct_card" ? tr("External card") :
                        methodKey === "bank_transfer" ? tr("Bank Transfer") :
                        methodKey === "cash" ? tr("Cash") :
                        (gp.method || "-");

                      const typeTxt =
                        actionKey === "preauthorize" ? tr("Preauthorization") :
                        actionKey === "collect" ? tr("Final charge") :
                        actionKey === "capture" ? (txTab === "active" ? tr("Final charge") : tr("Capture")) :
                        actionKey === "release" ? tr("Release") :
                        actionKey === "refund" ? tr("Refund") :
                        (gp.action || "-");

                      // Stripe-only: to show buttons we need the intentRow (because endpoints expect intentRowId)
                      const stripePi =
                        isStripeRow && providerRef
                          ? stripePiByPaymentIntentId.get(providerRef) || null
                          : null;

                      const isStripeManual =
                        stripePi && String(stripePi.capture_method || "").toLowerCase() === "manual";

                      const isPreauthOpen =
                        stripePi &&
                        isStripeManual &&
                        !!stripePi.authorized_at &&
                        !stripePi.captured_at &&
                        !stripePi.canceled_at;

                      const isCaptured =
                        stripePi &&
                        !!stripePi.captured_at &&
                        !stripePi.canceled_at;

                      const isReleased =
                        stripePi &&
                        !!stripePi.canceled_at &&
                        !stripePi.captured_at;

                      const rowAmountCents = Number(gp.amount_cents || 0);

                      const refundSummary = summarizeRefundEvents(stripePi?.events);
                      const refundedCentsForThisPI = refundSummary.confirmedCents;

                      const remainingCents = Math.max(0, rowAmountCents - refundedCentsForThisPI);

                      const isFullyRefunded =
                        rowAmountCents > 0 && refundedCentsForThisPI >= rowAmountCents;

                      const isPartiallyRefunded =
                        rowAmountCents > 0 && refundedCentsForThisPI > 0 && refundedCentsForThisPI < rowAmountCents;

                      // Active: if partially refunded, show only the "live" amount
                      const amountCentsToShow =
                        txTab === "active" &&
                        isStripeRow &&
                        isCaptured &&
                        (actionKey === "collect" || actionKey === "capture") &&
                        isPartiallyRefunded
                          ? remainingCents
                          : rowAmountCents;

                      const amountTxt = formatTenderCents(
                        amountCentsToShow,
                        String(gp.currency || baseCurrency).toUpperCase()
                      );

                      const canCapture = canEditStripePayments && !!stripePi && isPreauthOpen;
                      const canRelease = canEditStripePayments && !!stripePi && isPreauthOpen;

                      // Refund button only if captured and something is still "live"
                      const canRefund =
                        canEditStripePayments &&
                        !!stripePi &&
                        isCaptured &&
                        (actionKey === "collect" || actionKey === "capture") &&
                        !isFullyRefunded &&
                        !refundSummary.hasOpenRefund &&
                        remainingCents > 0;


                      let statusTxt = String(gp.status || "-");
                      if (txTab === "active") {
                        if (isPreauthOpen) statusTxt = "Authorized (not captured)";

                        if (isCaptured && actionKey !== "preauthorize") {
                          if (refundSummary.needsReview) statusTxt = "Refund needs review";
                          else if (refundSummary.hasOpenRefund) statusTxt = "Refund pending";
                          else if (isPartiallyRefunded) statusTxt = "Partially refunded";
                          else statusTxt = "Captured";
                        }
                      } else {

                        // HISTORY: show outcome, not the raw ledger row status.
                        if (isStripeRow) {
                          if (actionKey === "preauthorize") {
                            if (isReleased) statusTxt = "Released";
                            else if (isCaptured) statusTxt = "Captured";
                            else if (isPreauthOpen) statusTxt = "Authorized (not captured)";
                          } else if (actionKey === "release") {
                            statusTxt = "Released";
                          } else if (actionKey === "capture") {
                            const s = String(gp.status || "").toLowerCase();
                            if (s === "processing") statusTxt = "Processing";
                            else statusTxt = "Captured";
                          } else if (actionKey === "refund") {
                            statusTxt = refundStatusLabel(gp.status);
                          } else if (actionKey === "collect") {
                            const s = String(gp.status || "").toLowerCase();
                            if (s === "processing") statusTxt = "Processing";
                            else if (s === "requires_action") statusTxt = "Requires action";
                            else if (s === "requires_payment_method") statusTxt = "Payment failed";
                            else if (s === "canceled") statusTxt = "Canceled";
                            else statusTxt = "Captured";
                          }
                        }
                      }

                      let historyDesc = String(gp.note || "").trim() || "-";
                      if (txTab === "history" && actionKey === "preauthorize") {
                        if (statusTxt === "Released") historyDesc = `${historyDesc} → ${tr("Released")}`;
                        else if (statusTxt === "Captured") historyDesc = `${historyDesc} → ${tr("Captured")}`;
                      }

                      const dt = gp.created_at || null;

                      return (
                        <tr key={gp.id}>
                          <td>{dt ? fmtDT(dt, locale) : "-"}</td>
                          <td>{tr(methodTxt)}</td>
                          <td>{tr(typeTxt)}</td>
                          <td className="fw-bold">{amountTxt}</td>
                          <td>{tr(translateReservationStatus(statusTxt, languageId))}</td>

                          <td>
                            {txTab === "history" ? (
                              <span className="text-secondary" style={{ fontSize: "0.85rem" }}>
                                {historyDesc}
                              </span>
                            ) : (
                              <div className="d-flex flex-wrap gap-2">
                                {canCapture ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-success fw-bold px-3"
                                    disabled={isWorking}
                                    onClick={() => {
                                      console.log("[PAYMENTS][CAPTURE][UI][OPEN]", { payment_intent_id: stripePi.id });
                                      setCaptureModal({ isOpen: true, payment_intent_id: stripePi.id });
                                      setCaptureModalAmount("");
                                    }}
                                  > {tr("Capture")} </button>
                                ) : null}


                                {canRelease ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-warning fw-bold px-3"
                                    disabled={isWorking}
                                    onClick={() => doRelease(stripePi.id)}
                                  > {tr("Release")} </button>
                                ) : null}

                                {canRefund ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger fw-bold px-3"
                                    disabled={isWorking}
                                    onClick={() => {
                                      console.log("[PAYMENTS][REFUND][UI][OPEN]", {
                                        payment_intent_id: stripePi.id,
                                        current_value: refundAmountByIntentId?.[stripePi.id] ?? "",
                                      });

                                      setRefundModal((prev) => ({
                                        ...prev,
                                        isOpen: true,
                                        payment_intent_id: stripePi.id,
                                      }));

                                      setRefundModalAmount(String(refundAmountByIntentId?.[stripePi.id] || "").trim());
                                    }}
                                  > {tr("Refund")} </button>
                                ) : null}


                                {!canCapture && !canRelease && !canRefund ? (
                                  <span className="text-secondary" style={{ fontSize: "0.85rem" }}>
                                    -
                                  </span>
                                ) : null}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                      <tr>
                        <td colSpan={6} className="text-secondary">{tr(isReservationScope ? "No transactions found for this reservation." : "No transactions found for this group.")}</td>
                      </tr>
                  )}
                </tbody>

              </table>
            </div>

            {txTab === "active" ? (
              <div className="mp-panel-note"> {tr("Tip: Use")} <span className="fw-bold">{tr("Capture")}</span> {tr("to charge a preauthorization, or")}{" "}
                <span className="fw-bold">{tr("Release")}</span> {tr("to unblock it.")} </div>
            ) : null}
          </div>

        </div>
      </div>

      {allocationModal.isOpen ? (
        <div
          className="inv-popup-overlay"
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.58)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            zIndex: 23000,
          }}
        >
          <div
            className="inv-popup-container"
            style={{
              width: "95%",
              maxWidth: "860px",
              maxHeight: "90vh",
              overflowY: "auto",
              backgroundColor: "#111",
              border: "1px solid #444",
              borderRadius: "10px",
              padding: "14px",
              color: "#fff",
            }}
          >
            <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
              <div>
                <div className="fw-bold" style={{ fontSize: "1.0rem" }}>
                  {tr(isReservationScope
                    ? (isAllocationRefund ? "Confirm reservation refund" : "Confirm reservation payment")
                    : (isAllocationRefund ? "Allocate Refund" : "Allocate Payment"))}
                </div>
                <div className="text-secondary" style={{ fontSize: "0.86rem" }}>
                  {tr(allocationModal.methodLabel)} · {formatTenderCents(allocationModal.amountCents, allocationCurrency)}
                </div>
              </div>

              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={isWorking}
                onClick={() => {
                  console.log("[PAYMENTS][ALLOCATION][UI][CLOSE]");
                  setAllocationModal({
                    isOpen: false,
                    kind: "payment",
                    method: null,
                    methodLabel: "",
                    currency: null,
                    amountCents: 0,
                    rows: [],
                    edited: false,
                  });
                }}
              > {tr("Close")} </button>
            </div>

            <div className="table-responsive mp-table-wrapper" style={{ maxHeight: "360px" }}>
              <table className="table table-dark table-striped table-sm align-middle mb-0 mp-popup-table mp-allocationTable">
                <thead>
                  <tr>
                    <th>{tr("Reservation")}</th>
                    <th style={{ width: "130px" }}>{tr("Total")}</th>
                    <th style={{ width: "130px" }}>{tr("Paid")}</th>
                    <th style={{ width: "130px" }}>{tr("Pending")}</th>
                    <th style={{ width: "160px" }}>{isAllocationRefund ? tr("Refund") : tr("Apply")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(allocationModal.rows || []).length ? (
                    (allocationModal.rows || []).map((row) => (
                      <tr key={row.reservation_id}>
                        <td>
                          <div className="fw-bold">{row.label || tr("Reservation #{id}", { id: row.reservation_id })}</div>
                        </td>
                        <td>{formatTenderCents(row.total_cents, allocationCurrency)}</td>
                        <td>{formatTenderCents(row.allocated_cents, allocationCurrency)}</td>
                        <td>{formatTenderCents(row.due_cents, allocationCurrency)}</td>
                        <td>
                          <input
                            type="number"
                            inputMode={allocationMinorUnits > 0 ? "decimal" : "numeric"}
                            step={currencyAmountStep(allocationCurrency, currencyCatalog)}
                            className="form-control form-control-sm"
                            value={row.inputValue}
                            readOnly={isReservationScope}
                            aria-label={tr(isAllocationRefund ? "Refund" : "Apply")}
                            disabled={isWorking}
                            onChange={(e) => updateAllocationInput(row.reservation_id, e.target.value)}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-secondary">
                        {isAllocationRefund
                          ? tr("No paid reservation balances.")
                          : tr("No pending reservation balances.")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className={`mp-allocationFooter${isReservationScope ? " mp-allocationFooter--reservation" : ""}`}>
              <div className="mp-balanceMetric">
                <div className="mp-balanceLabel">{isAllocationRefund ? tr("Refund") : tr("Payment")}</div>
                <div className="mp-balanceValue">{formatTenderCents(allocationModal.amountCents, allocationCurrency)}</div>
              </div>
              <div className="mp-balanceMetric">
                <div className="mp-balanceLabel">{isAllocationRefund ? tr("Applied") : tr("Assigned")}</div>
                <div className="mp-balanceValue">{formatTenderCents(allocationAllocatedCents, allocationCurrency)}</div>
              </div>
              {!isReservationScope ? <div className="mp-balanceMetric">
                <div className="mp-balanceLabel">{tr("Unallocated")}</div>
                <div className="mp-balanceValue">{formatTenderCents(allocationUnallocatedCents, allocationCurrency)}</div>
              </div> : null}
            </div>

            {allocationOverCents > 0 ? (
              <div className="alert alert-danger py-2 mt-3 mb-0">
                {tr(isAllocationRefund ? "Applied amount exceeds refund by {amount}." : "Assigned amount exceeds payment by {amount}.", {
                  amount: formatTenderCents(allocationOverCents, allocationCurrency),
                })}
              </div>
            ) : null}

            <div className="d-flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                className="btn btn-sm btn-outline-success fw-bold px-3"
                disabled={isWorking || !scopeReady || allocationOverCents > 0}
                onClick={() => submitManualPaymentWithAllocations({ leaveUnallocated: false })}
              >
                {tr(isReservationScope
                  ? (isAllocationRefund ? "Confirm refund" : "Confirm payment")
                  : (isAllocationRefund ? "Save refund" : "Save allocation"))}
              </button>

              {!isReservationScope ? <button
                type="button"
                className="btn btn-sm btn-outline-warning fw-bold px-3"
                disabled={isWorking}
                onClick={() => submitManualPaymentWithAllocations({ leaveUnallocated: true })}
              >
                {isAllocationRefund ? tr("Leave refund unallocated") : tr("Leave unallocated")}
              </button> : null}

              <button
                type="button"
                className="btn btn-sm btn-outline-secondary fw-bold px-3"
                disabled={isWorking}
                onClick={() => {
                  setAllocationModal({
                    isOpen: false,
                    kind: "payment",
                    method: null,
                    methodLabel: "",
                    currency: null,
                    amountCents: 0,
                    rows: [],
                    edited: false,
                  });
                }}
              > {tr("Cancel")} </button>
            </div>
          </div>
        </div>
      ) : null}

      {refundModal.isOpen ? (
        <div
            className="inv-popup-overlay"
            style={{
              position: "fixed",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0,0,0,0.55)",
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              zIndex: 23000,
            }}
          >
            <div
              className="inv-popup-container"
              style={{
                width: "95%",
                maxWidth: "480px",
                maxHeight: "90vh",
                overflowY: "auto",
                backgroundColor: "#111",
                border: "1px solid #444",
                borderRadius: "10px",
                padding: "14px",
                color: "#fff",
              }}
            >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="fw-bold" style={{ fontSize: "1.0rem" }}> {tr("Refund")} </div>

              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={isWorking}
                onClick={() => {
                  console.log("[PAYMENTS][REFUND][UI][CLOSE]");
                  setRefundModal({ isOpen: false, payment_intent_id: null });
                  setRefundModalAmount("");
                }}
              > {tr("Close")} </button>
            </div>

            <div className="text-secondary mb-2" style={{ fontSize: "0.9rem" }}> {tr("Enter an amount in EUR for a partial refund, or use")} <span className="fw-bold">{tr("Full refund")}</span>.
            </div>

            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={tr("e.g. 25.50 (leave blank for full refund)")}
              value={refundModalAmount}
              onChange={(e) => setRefundModalAmount(String(e.target.value || ""))}
              disabled={isWorking}
            />

            <div className="d-flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                className="btn btn-sm btn-outline-danger fw-bold px-3"
                disabled={isWorking || !refundModal.payment_intent_id}
                onClick={() => {
                  const piId = refundModal.payment_intent_id;

                  console.log("[PAYMENTS][REFUND][UI][FULL_REFUND][CLICK]", { payment_intent_id: piId });

                  setRefundAmountByIntentId((prev) => ({ ...prev, [piId]: "" }));
                  doRefund(piId, null);


                  setRefundModal({ isOpen: false, payment_intent_id: null });
                  setRefundModalAmount("");
                }}
              > {tr("Full refund")} </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-warning fw-bold px-3"
                disabled={isWorking || !refundModal.payment_intent_id}
                onClick={() => {
                  const piId = refundModal.payment_intent_id;
                  const clean = String(refundModalAmount || "").trim();

                  console.log("[PAYMENTS][REFUND][UI][PARTIAL_REFUND][CLICK]", {
                    payment_intent_id: piId,
                    amount_eur: clean,
                  });

                  const n = Number(String(clean).replace(",", "."));
                  if (!Number.isFinite(n) || n <= 0) {
                    window.alert(tr("Invalid refund amount."));
                    return;
                  }

                  setRefundAmountByIntentId((prev) => ({ ...prev, [piId]: String(clean) }));
                  doRefund(piId, n);


                  setRefundModal({ isOpen: false, payment_intent_id: null });
                  setRefundModalAmount("");
                }}
              > {tr("Refund amount")} </button>
            </div>
          </div>
        </div>
      ) : null}

      {captureModal.isOpen ? (
        <div
          className="inv-popup-overlay"
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.55)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
              zIndex: 23000,
          }}
        >
          <div
            className="inv-popup-container"
            style={{
              width: "95%",
              maxWidth: "480px",
              maxHeight: "90vh",
              overflowY: "auto",
              backgroundColor: "#111",
              border: "1px solid #444",
              borderRadius: "10px",
              padding: "14px",
              color: "#fff",
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="fw-bold" style={{ fontSize: "1.0rem" }}> {tr("Capture")} </div>

              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={isWorking}
                onClick={() => {
                  console.log("[PAYMENTS][CAPTURE][UI][CLOSE]");
                  setCaptureModal({ isOpen: false, payment_intent_id: null });
                  setCaptureModalAmount("");
                }}
              > {tr("Close")} </button>
            </div>

            <div className="text-secondary mb-2" style={{ fontSize: "0.9rem" }}> {tr("Enter an amount in EUR for a partial capture, or use")} <span className="fw-bold">{tr("Full capture")}</span>.
            </div>

            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={tr("e.g. 50.00 (leave blank for full capture)")}
              value={captureModalAmount}
              onChange={(e) => setCaptureModalAmount(String(e.target.value || ""))}
              disabled={isWorking}
            />

            <div className="d-flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                className="btn btn-sm btn-outline-success fw-bold px-3"
                disabled={isWorking || !captureModal.payment_intent_id}
                onClick={() => {
                  const piId = captureModal.payment_intent_id;

                  console.log("[PAYMENTS][CAPTURE][UI][FULL_CAPTURE][CLICK]", { payment_intent_id: piId });

                  doCapture(piId, null);

                  setCaptureModal({ isOpen: false, payment_intent_id: null });
                  setCaptureModalAmount("");
                }}
              > {tr("Full capture")} </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-warning fw-bold px-3"
                disabled={isWorking || !captureModal.payment_intent_id}
                onClick={() => {
                  const piId = captureModal.payment_intent_id;
                  const clean = String(captureModalAmount || "").trim();

                  console.log("[PAYMENTS][CAPTURE][UI][PARTIAL_CAPTURE][CLICK]", {
                    payment_intent_id: piId,
                    amount_eur: clean,
                  });

                  const n = Number(String(clean).replace(",", "."));
                  if (!Number.isFinite(n) || n <= 0) {
                    window.alert(tr("Invalid capture amount."));
                    return;
                  }

                  doCapture(piId, n);

                  setCaptureModal({ isOpen: false, payment_intent_id: null });
                  setCaptureModalAmount("");
                }}
              > {tr("Capture amount")} </button>
            </div>
          </div>
        </div>
      ) : null}



    </div>
  </>
  );

  if (typeof document === "undefined") return null;
  return createPortal(popupContent, document.body);
}
